import {
  candidateGscProperties,
  SERP_COUNTRIES,
  SERP_DEVICES,
  SERP_LANGUAGES,
  type SerpCountry,
  type SerpDevice,
  type SerpLanguage,
  type SerpMarket,
} from '@seoplum/core'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { Panel } from '@/components/panel'
import { SearchConsoleStatus } from '@/components/status-dot'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDate } from '@/lib/format'
import { type Site, sitesQuery, useSetSiteMarket } from '@/lib/queries'
import { countryName, languageName } from '@/lib/serp'
import { DeleteSiteDialog, RenameSiteDialog } from '../-site-list'
import { useSiteScope } from './-section'

export const Route = createFileRoute('/_app/$org/$host/settings')({
  component: SiteSettingsPage,
})

function SiteSettingsPage() {
  const { org, site } = useSiteScope()
  const { data } = useQuery(sitesQuery)
  const navigate = useNavigate()
  const [renaming, setRenaming] = useState<Site | null>(null)
  const [deleting, setDeleting] = useState<Site | null>(null)

  if (!site) return null

  const parent = data?.sites.find((candidate) => candidate.id === site.parentSiteId)

  return (
    <>
      {/* The tab row names the section and the switcher names the site; the
          heading is here for the document outline, not for the eye. */}
      <h1 className="sr-only">Site settings</h1>

      <div className="grid gap-4 sm:grid-cols-2">
        <Panel title="Details">
          <dl className="space-y-3 text-[13px]">
            <Row label="Name" value={site.displayName} />
            <Row label="Domain" value={<span className="font-mono">{site.host}</span>} />
            <Row
              label="Added"
              value={<span className="font-mono">{formatDate(site.createdAt)}</span>}
            />
            <Row
              label="Property"
              value={
                site.propertyUri ? (
                  <span className="font-mono">{site.propertyUri}</span>
                ) : (
                  <span className="text-muted-foreground">Not linked</span>
                )
              }
            />
            {parent && (
              <Row
                label="Covered by"
                value={
                  <Link
                    to="/$org/$host"
                    params={{ org, host: parent.host }}
                    className="font-mono text-link underline-offset-4 hover:underline"
                  >
                    {parent.host}
                  </Link>
                }
              />
            )}
          </dl>
        </Panel>

        <Panel title="Search Console">
          <SearchConsoleStatus status={site.searchConsole} email={site.connectionEmail} />

          {site.searchConsole === 'not-connected' && (
            <>
              <p className="pt-3 text-[13px] text-muted-foreground">
                Connect Google to pull impressions, clicks and query data for this domain. Until
                then this site has no data behind it.
              </p>
              <div className="pt-4">
                <Button asChild size="sm" variant="outline">
                  <Link to="/$org/settings/connections" params={{ org }}>
                    Connect Search Console
                  </Link>
                </Button>
              </div>
              <p className="pt-3 text-[12px] text-muted-foreground">
                Likely properties:{' '}
                <span className="font-mono">
                  {candidateGscProperties(site.host)
                    .map((property) => property.uri)
                    .join('  ')}
                </span>
              </p>
            </>
          )}
        </Panel>

        <Panel title="Market" className="sm:col-span-2">
          <MarketPicker site={site} />
        </Panel>

        <Panel title="Danger zone" className="sm:col-span-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setRenaming(site)}>
              Rename
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-destructive"
              onClick={() => setDeleting(site)}
            >
              Delete site
            </Button>
          </div>
        </Panel>
      </div>

      <RenameSiteDialog site={renaming} onClose={() => setRenaming(null)} />
      <DeleteSiteDialog
        site={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => navigate({ to: '/$org', params: { org } })}
      />
    </>
  )
}

/**
 * Where this site's rankings are read.
 *
 * A site setting rather than a control on the star, because a market is a
 * property of who the site is trying to reach and not of one keyword — and
 * because the star is already the control that spends, which is enough for one
 * button to mean. Changing it governs new stars and every unqualified read;
 * keywords already watched in another market keep it, since the readings behind
 * them were taken there.
 *
 * Each select saves on change. There is no save button because there is nothing
 * to review: one field, one value, and the next read uses it.
 */
function MarketPicker({ site }: { site: Site }) {
  const setMarket = useSetSiteMarket()
  const market = site.serpMarket

  async function change(next: SerpMarket) {
    try {
      await setMarket.mutateAsync({ id: site.id, market: next })
      toast.success(`Reading ${site.host} in ${countryName(next.gl)}`)
    } catch {
      toast.error('Could not change the market')
    }
  }

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Axis label="Country">
        <Select
          value={market.gl}
          onValueChange={(value) => change({ ...market, gl: value as SerpCountry })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERP_COUNTRIES.map((gl) => (
              <SelectItem key={gl} value={gl}>
                <span className="flex w-full items-baseline gap-2">
                  {countryName(gl)}
                  <span className="font-mono text-[11px] text-muted-foreground">{gl}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Axis>

      <Axis label="Language">
        <Select
          value={market.hl}
          onValueChange={(value) => change({ ...market, hl: value as SerpLanguage })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERP_LANGUAGES.map((hl) => (
              <SelectItem key={hl} value={hl}>
                <span className="flex w-full items-baseline gap-2">
                  {languageName(hl)}
                  <span className="font-mono text-[11px] text-muted-foreground">{hl}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Axis>

      <Axis label="Device">
        <Select
          value={market.device}
          onValueChange={(value) => change({ ...market, device: value as SerpDevice })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERP_DEVICES.map((device) => (
              <SelectItem key={device} value={device}>
                {device === 'desktop' ? 'Desktop' : 'Mobile'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Axis>
    </div>
  )
}

function Axis({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="mono-label block text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="mono-label text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  )
}
